import { type FC, useMemo } from 'react';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';

import { IconLoader2 } from '@tabler/icons-react';
import { Tag } from '@wk/components-react16';

import {
  CellValueTooltip,
  StatusTooltip,
} from '@/features/settings/pages/CheckpointSettingsPage/CheckpointSettingsTooltips';
import type { CheckpointSystemStatus } from '@/types';

import SiteCheckpointSettingsActions from './SiteCheckpointSettingsActions';
import type {
  SiteCheckpointRow,
  SiteDateEditKind,
  SiteTagColor,
} from './SiteCheckpointSettingsPage.types';
import {
  CHECKPOINT_STATUS_MAP,
  formatStatusDate,
  getStatusTooltipLines,
} from './SiteCheckpointSettingsPage.utils';

export interface SiteCheckpointSettingsContentProps {
  checkpoints: SiteCheckpointRow[];
  isLoading: boolean;
  isMobileViewport: boolean;
  canManageCheckpoints: boolean;
  mutationTooltip?: string | null;
  onOpenMoveModal: (
    row: SiteCheckpointRow,
    targetStatus: CheckpointSystemStatus,
  ) => void;
  onOpenEditDateModal: (row: SiteCheckpointRow, kind: SiteDateEditKind) => void;
}

const SiteCheckpointSettingsContent: FC<SiteCheckpointSettingsContentProps> = ({
  checkpoints,
  isLoading,
  isMobileViewport,
  canManageCheckpoints,
  mutationTooltip,
  onOpenMoveModal,
  onOpenEditDateModal,
}) => {
  const actionsRenderer = (
    row: SiteCheckpointRow | null | undefined,
    context: 'grid' | 'card',
  ) => (
    <SiteCheckpointSettingsActions
      row={row}
      context={context}
      canManageCheckpoints={canManageCheckpoints}
      isMobileViewport={isMobileViewport}
      mutationTooltip={mutationTooltip}
      onOpenMoveModal={onOpenMoveModal}
      onOpenEditDateModal={onOpenEditDateModal}
    />
  );

  const columnDefs = useMemo(
    (): ColDef<SiteCheckpointRow>[] => [
      {
        headerName: 'Checkpoint Id',
        field: 'id',
        width: 210,
        minWidth: 170,
      },
      {
        headerName: 'Version',
        field: 'version',
        width: 100,
        valueGetter: (params: { data?: SiteCheckpointRow }) => {
          const version = params?.data?.version;
          return version === null || version === undefined ? '' : `${version}`;
        },
      },
      {
        headerName: 'Checkpoint Name',
        field: 'displayName',
        width: 200,
        minWidth: 160,
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 130,
        minWidth: 120,
        tooltipComponent: StatusTooltip,
        tooltipValueGetter: (params: { data?: SiteCheckpointRow }) => {
          if (isMobileViewport) return null;
          const lines = getStatusTooltipLines(params?.data);
          if (lines.length > 0) return lines.join('\n');
          return params?.data?.status ?? null;
        },
        cellRenderer: (params: CustomCellRendererProps<SiteCheckpointRow>) => {
          const checkpoint = params.data;
          if (!checkpoint) return null;
          const entry =
            CHECKPOINT_STATUS_MAP[checkpoint.status] ??
            CHECKPOINT_STATUS_MAP.LIVE;
          const color = entry.color as SiteTagColor;

          return <Tag color={color}>{entry.label}</Tag>;
        },
      },
      {
        headerName: 'End Date',
        width: 130,
        valueGetter: (params: { data?: SiteCheckpointRow }) => {
          return formatStatusDate(params?.data?.statusTooltip?.endDate);
        },
      },
      {
        headerName: 'Accountable',
        field: 'accountable',
        width: 140,
      },
      {
        headerName: 'Reference System',
        field: 'referenceSystem',
        width: 160,
      },
      {
        headerName: 'Description',
        field: 'description',
        flex: 1,
        minWidth: 180,
      },
      {
        headerName: 'Actions',
        pinned: 'right' as const,
        width: 96,
        cellRenderer: (params: CustomCellRendererProps<SiteCheckpointRow>) => {
          return actionsRenderer(params.data, 'grid');
        },
      },
    ],
    [
      actionsRenderer,
      canManageCheckpoints,
      isMobileViewport,
      mutationTooltip,
    ],
  );

  if (isMobileViewport) {
    return (
      <div className="checkpoints-cards-container pr-1">
        {isLoading ? (
          <div className="flex h-full min-h-[220px] items-center justify-center text-gray-500">
            <IconLoader2 size={22} className="animate-spin" />
          </div>
        ) : checkpoints.length === 0 ? (
          <div className="rounded-md border bg-white p-4 text-sm text-gray-600">
            No checkpoints found.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {checkpoints.map((checkpoint) => {
              const statusEntry =
                CHECKPOINT_STATUS_MAP[checkpoint.status] ??
                CHECKPOINT_STATUS_MAP.LIVE;
              const statusColor = statusEntry.color as SiteTagColor;
              const statusLines = getStatusTooltipLines(checkpoint);
              const endDate = formatStatusDate(checkpoint.statusTooltip?.endDate);
              const version =
                checkpoint.version === null || checkpoint.version === undefined
                  ? 'N/A'
                  : `${checkpoint.version}`;
              const cardKey = `${checkpoint.id}-${version}`;

              return (
                <article
                  key={cardKey}
                  className="rounded-lg border bg-white p-3 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="break-words text-base font-semibold leading-5">
                        {checkpoint.displayName}
                      </h2>
                      <p className="mt-1 text-xs text-gray-500">
                        ID {checkpoint.id} | v{version}
                      </p>
                    </div>
                    {actionsRenderer(checkpoint, 'card')}
                  </div>

                  <dl className="mt-3 grid grid-cols-1 gap-2 text-sm">
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                        Description
                      </dt>
                      <dd className="break-words">
                        {checkpoint.description || 'N/A'}
                      </dd>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                          Accountable
                        </dt>
                        <dd>{checkpoint.accountable || 'N/A'}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                          Reference
                        </dt>
                        <dd>{checkpoint.referenceSystem || 'N/A'}</dd>
                      </div>
                    </div>
                  </dl>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Tag color={statusColor}>{statusEntry.label}</Tag>
                    {endDate ? (
                      <span className="text-xs text-gray-600">End: {endDate}</span>
                    ) : null}
                  </div>

                  {statusLines.length > 0 ? (
                    <div className="mt-2 rounded-md bg-gray-50 px-2 py-2 text-xs text-gray-600">
                      {statusLines.map((line) => (
                        <p key={`${cardKey}-${line}`} className="break-words">
                          {line}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <div className="cg-ag-grid-field ag-theme-balham h-full w-full">
        <div className="h-full w-full">
          <AgGridReact
            loading={isLoading}
            popupParent={typeof document === 'undefined' ? undefined : document.body}
            tooltipShowDelay={0}
            tooltipShowMode="whenTruncated"
            rowData={checkpoints}
            defaultColDef={{
              resizable: true,
              tooltipComponent: CellValueTooltip,
              tooltipValueGetter: (params: {
                value?: unknown;
                valueFormatted?: unknown;
              }) => {
                if (isMobileViewport) return null;
                const value = params?.valueFormatted ?? params?.value;
                if (value === null || value === undefined) return null;
                const text = `${value}`.trim();
                return text.length ? text : null;
              },
            }}
            columnDefs={columnDefs}
          />
        </div>
      </div>
    </div>
  );
};

export default SiteCheckpointSettingsContent;
